import { type FormEvent, useEffect, useState } from 'react';
import type { ResourceArticleRecord } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

type ArticleDifficulty = 'beginner' | 'intermediate' | 'advanced';

const emptyForm = {
  slug: '',
  title: '',
  description: '',
  category: 'financing',
  difficulty: 'beginner' as ArticleDifficulty,
  read_minutes: 8,
  body_md: '',
  published: true,
  archived: false,
  tags: 'investing, multifamily',
};

export function AdminResources() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [rows, setRows] = useState<ResourceArticleRecord[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    if (!token) return;
    const r = await apiFetch<{ articles: ResourceArticleRecord[] }>('/api/staff/resource-articles', { token });
    setRows(r.articles);
  }

  useEffect(() => {
    void refresh().catch(() => {});
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setErr(null);
    try {
      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const base = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        difficulty: form.difficulty,
        read_minutes: form.read_minutes,
        body_md: form.body_md,
        published: form.published,
        archived: form.archived,
        tags,
      };
      if (editingId) {
        await apiFetch(`/api/staff/resource-articles/${editingId}`, { method: 'PATCH', token, body: JSON.stringify(base) });
      } else {
        await apiFetch('/api/staff/resource-articles', {
          method: 'POST',
          token,
          body: JSON.stringify({ ...base, slug: form.slug.trim() }),
        });
      }
      setForm(emptyForm);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  function startEdit(a: ResourceArticleRecord) {
    setEditingId(a.id);
    setForm({
      slug: a.slug,
      title: a.title,
      description: a.description,
      category: a.category,
      difficulty: a.difficulty as ArticleDifficulty,
      read_minutes: a.read_minutes,
      body_md: a.body_md,
      published: a.published,
      archived: a.archived,
      tags: a.tags.join(', '),
    });
  }

  async function remove(id: string) {
    if (!token) return;
    if (!window.confirm('Delete this article?')) return;
    await apiFetch(`/api/staff/resource-articles/${id}`, { method: 'DELETE', token });
    await refresh();
  }

  return (
    <div className="max-w-4xl space-y-10">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Resource articles</h1>
        <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">Create, update, archive, or remove knowledge base posts.</p>
      </div>

      <form className={`space-y-4 p-6 ${SITE_GLASS_CARD}`} onSubmit={(ev) => void onSubmit(ev)}>
        <h2 className="text-sm font-semibold text-[var(--home-text-on-dark)]">{editingId ? 'Edit article' : 'New article'}</h2>
        {err ? <p className="text-sm text-rose-300">{err}</p> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Slug</span>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
              required
              disabled={Boolean(editingId)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Title</span>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`} required />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Description</span>
          <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <label className="block text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Body (Markdown)</span>
          <textarea rows={10} value={form.body_md} onChange={(e) => setForm((f) => ({ ...f, body_md: e.target.value }))} className={`mt-1 w-full font-mono text-xs ${SITE_FORM_CONTROL}`} />
        </label>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Category</span>
            <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`} />
          </label>
          <label className="block text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Difficulty</span>
            <select value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value as typeof form.difficulty }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`}>
              <option value="beginner">beginner</option>
              <option value="intermediate">intermediate</option>
              <option value="advanced">advanced</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-[var(--home-muted-on-dark)]">Read minutes</span>
            <input type="number" min={1} value={form.read_minutes} onChange={(e) => setForm((f) => ({ ...f, read_minutes: Number(e.target.value) }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`} />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-[var(--home-muted-on-dark)]">Tags (comma)</span>
          <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className={`mt-1 w-full ${SITE_FORM_CONTROL}`} />
        </label>
        <div className="flex gap-4 text-sm text-[var(--home-text-on-dark)]">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
            Published
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.archived} onChange={(e) => setForm((f) => ({ ...f, archived: e.target.checked }))} />
            Archived
          </label>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2 text-sm font-semibold text-[var(--home-zone-void)]">
            {editingId ? 'Save changes' : 'Create article'}
          </button>
          {editingId ? (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm);
              }}
              className="rounded-xl border border-white/15 px-5 py-2 text-sm"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      <section className={`${SITE_GLASS_CARD} overflow-hidden`}>
        <header className="border-b border-white/[0.08] px-5 py-3 text-sm font-semibold">All articles</header>
        <ul className="divide-y divide-white/[0.08]">
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
              <div>
                <p className="font-medium text-[var(--home-text-on-dark)]">{a.title}</p>
                <p className="text-xs text-[var(--home-muted-on-dark)]">
                  /{a.slug} · {a.archived ? 'archived' : 'live'}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => startEdit(a)} className="rounded-lg bg-white/10 px-3 py-1 text-xs">
                  Edit
                </button>
                <button type="button" onClick={() => void remove(a.id)} className="rounded-lg border border-rose-500/40 px-3 py-1 text-xs text-rose-300">
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
